import cloneDeep from 'lodash.clonedeep';
import {
  FormTemplate,
  FormTemplateField,
  FormTemplateOption,
  FormTemplateSection,
} from 'data/template/template.types';
import {
  AccessFunction,
  AccessFunctionFormData,
  AccessFunctionInterfaceFormData,
} from 'data/accessFunction/accessFunction.types';
import { NodeData } from 'data/nodes/nodes.types';
import semver from 'semver';
import _ from 'lodash';
import { Network } from 'data/network/network.types';
import { Buffer } from 'buffer';
import { SERVER_AF_TYPES } from 'data/accessFunction/accessFunction.adapter';
import {
 getNetworkOptionsMap, getXcipioNodesByType, setOptionForTemplateSection, setOptionsForFields, setOptionsForLiX1Fields
} from './accessFunctionTemplate.adapter';

enum AFTypes {
  CasaSMF = 'CASA_5GSMF',
  EricssonAMF = 'ERK_5GAMF',
  NokiaAMF = 'NOK_5GAMF',
  Ericsson5GNEF = 'ERK_5GNEF',
  Ericsson5GSMF = 'ERK_5GSMF',
  Mavenir5GMMSC = 'MVNR_5GMMSC',
  MavenirLimaasMgr = 'MVNR_LIMAAS_MGR',
  MavenirPACOCMS = 'MVNR_PACO_CMS',
  MavenirIMSCMS = 'MVNR_IMS_CMS',
  MavenirSMSF = 'MVNR_5GSMSF',
  Nokia5GMMSC = 'NOK_5GMMSC',
  NokiaSMSF = 'NOK_5GSMSF',
  NokiaSMF = 'NOK_5GSMF',
  NokiaUDM = 'NOK_5GUDM',
  NokiaTAS = 'NOK_TAS',
  SS8CCPAG = 'SS8_CCPAG',
  SS8XCF3 = 'SS8_XCF3',
  SS8MDF2 = 'SS8_MDF2',
  SS8MDF3 = 'SS8_MDF3',
  ETSI102232 = 'ETSI102232',
  NgVoiceLICF = 'NGV_LICF',
  MobolizeVPN = 'MOBOLIZE_VPN',
}

export function adaptTemplate5GAFsForGROnLoad(
  template: FormTemplate | undefined,
  fieldName: keyof AccessFunctionInterfaceFormData,
  fieldValue: unknown,
  data: Partial<AccessFunctionFormData>,
  xcipioNodes?: NodeData[],
  networks?: Network[],
  accessFunctions?: AccessFunction[],
  originalTemplates?: { [key: string]: FormTemplate; } | undefined
): void {
  if (template == null || !originalTemplates || fieldValue === undefined) {
    return;
  }
  template.formId = Date.now().toString();
  const originalTemplate = originalTemplates[template.key] ?? undefined;
  const ccpagOptions = getXcipioNodesByType(xcipioNodes ?? [], ['CCPAG']);
  const xcf3Options = getXcipioNodesByType(xcipioNodes ?? [], ['XCF3']);
  const mdf2Options = getXcipioNodesByType(xcipioNodes ?? [], ['MDF2', 'XCP']);
  const mdf3Options = getXcipioNodesByType(xcipioNodes ?? [], ['MDF3', 'XCP']);
  if (template.key === AFTypes.SS8CCPAG || template.key === AFTypes.SS8MDF2 || template.key === AFTypes.SS8MDF3 || template.key === AFTypes.SS8XCF3) {
    const templateSection = (template.provisioningInterfaces as FormTemplateSection);
    const nodeNameToNodeTypeMap = (xcipioNodes ?? []).reduce((acc, curr) => {
      acc[curr.nodeName] = curr.nodeType;
      return acc;
    }, {} as { [key: string]: string });
    if (_.isArray(data.provisioningInterfaces) && data.provisioningInterfaces[0].role === 'CLIENT') {
      setOptionsForLiX1Fields(template, accessFunctions ?? [], networks ?? [], ccpagOptions, xcf3Options, mdf2Options, mdf3Options);
    } else {
      const nodeTypes = templateSection.fields.ownnetid.netidNodeTypes ?? undefined;
      const x1Networks = nodeTypes ? (networks ?? []).filter((n) => !n.nodeName || nodeTypes.includes(nodeNameToNodeTypeMap[n.nodeName])) : networks;
      setOptionForTemplateSection(templateSection, getNetworkOptionsMap(x1Networks ?? []), ['ownnetid']);
    }
  }
}

export function adaptTemplateAndDataFor5GAfsForGROnFieldUpdate(
  template: FormTemplate | undefined,
  fieldName: any,
  fieldValue: unknown,
  data: Partial<AccessFunctionFormData>,
  xcipioNodes?: NodeData[],
  networks?: Network[],
  accessFunctions?: AccessFunction[],
  originalTemplates?: { [key: string]: FormTemplate; } | undefined
): void {
  if (template == null || !originalTemplates || fieldValue === undefined) {
    return;
  }
  template.formId = Date.now().toString();
  const originalTemplate = originalTemplates[template.key] ?? undefined;
  switch (template.key) {
    case AFTypes.CasaSMF: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
        clearSelections(template, data);
        updateTemplateBasedOnDsidValue(template, fieldValue, data, xcipioNodes);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.EricssonAMF: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
        clearSelections(template, data);
        updateTemplateBasedOnDsidValue(template, fieldValue, data, xcipioNodes);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.NokiaAMF: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
        clearSelections(template, data);
        updateTemplateBasedOnDsidValue(template, fieldValue, data, xcipioNodes);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.Ericsson5GNEF: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
        clearSelections(template, data);
        updateTemplateBasedOnDsidValue(template, fieldValue, data, xcipioNodes);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.Ericsson5GSMF: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
        clearSelections(template, data);
        updateTemplateBasedOnDsidValue(template, fieldValue, data, xcipioNodes);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.Mavenir5GMMSC: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
        clearSelections(template, data);
        updateTemplateBasedOnDsidValue(template, fieldValue, data, xcipioNodes);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.MavenirLimaasMgr: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
      }
      break;
    }
    case AFTypes.MavenirSMSF: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
        clearSelections(template, data);
        updateTemplateBasedOnDsidValue(template, fieldValue, data, xcipioNodes);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.Nokia5GMMSC: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
        clearSelections(template, data);
        updateTemplateBasedOnDsidValue(template, fieldValue, data, xcipioNodes);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.NokiaSMF: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.NokiaUDM: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
        clearSelections(template, data);
        updateTemplateBasedOnDsidValue(template, fieldValue, data, xcipioNodes);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.NokiaSMSF: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
        clearSelections(template, data);
        updateTemplateBasedOnDsidValue(template, fieldValue, data, xcipioNodes);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.NokiaTAS: {
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.SS8CCPAG: {
      const formSection = (template.provisioningInterfaces as FormTemplateSection);
      if (fieldName === 'li_x1_ccpagnodename') {
        copyUriOfSelectedNodeName(fieldName, fieldValue, data, networks, accessFunctions);
        if (_.isArray(data.provisioningInterfaces) && data.provisioningInterfaces[0].role === 'SERVER') {
          filterNetworksBasedOnNodeNameSelection(fieldValue as string, data, networks, formSection);
        }
      }
      if (fieldName === 'role') {
        updateNodeNameFieldBasedOnRoleSelection(template, fieldValue, data, formSection.fields.li_x1_ccpagnodename, xcipioNodes, networks, accessFunctions, formSection);
      }
      break;
    }
    case AFTypes.SS8XCF3: {
      const formSection = (template.provisioningInterfaces as FormTemplateSection);
      if (fieldName === 'li_x1_xcf3nodename') {
        copyUriOfSelectedNodeName(fieldName, fieldValue, data, networks, accessFunctions);
        if (_.isArray(data.provisioningInterfaces) && data.provisioningInterfaces[0].role === 'SERVER') {
          filterNetworksBasedOnNodeNameSelection(fieldValue as string, data, networks, formSection);
        }
      }
      if (fieldName === 'role') {
        updateNodeNameFieldBasedOnRoleSelection(template, fieldValue, data, formSection.fields.li_x1_xcf3nodename, xcipioNodes, networks, accessFunctions, formSection);
      }
      break;
    }
    case AFTypes.SS8MDF2: {
      const formSection = (template.provisioningInterfaces as FormTemplateSection);
      if (fieldName === 'li_x1_mdf2nodename') {
        copyUriOfSelectedNodeName(fieldName, fieldValue, data, networks, accessFunctions);
        if (_.isArray(data.provisioningInterfaces) && data.provisioningInterfaces[0].role === 'SERVER') {
          filterNetworksBasedOnNodeNameSelection(fieldValue as string, data, networks, formSection);
        }
      }
      if (fieldName === 'role') {
        updateNodeNameFieldBasedOnRoleSelection(template, fieldValue, data, formSection.fields.li_x1_mdf2nodename, xcipioNodes, networks, accessFunctions, formSection);
      }
      break;
    }
    case AFTypes.SS8MDF3: {
      const formSection = (template.provisioningInterfaces as FormTemplateSection);
      if (fieldName === 'li_x1_mdf3nodename') {
        copyUriOfSelectedNodeName(fieldName, fieldValue, data, networks, accessFunctions);
        if (_.isArray(data.provisioningInterfaces) && data.provisioningInterfaces[0].role === 'SERVER') {
          filterNetworksBasedOnNodeNameSelection(fieldValue as string, data, networks, formSection);
        }
      }
      if (fieldName === 'role') {
        updateNodeNameFieldBasedOnRoleSelection(template, fieldValue, data, formSection.fields.li_x1_mdf3nodename, xcipioNodes, networks, accessFunctions, formSection);
      }
      break;
    }
    case AFTypes.NgVoiceLICF: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    case AFTypes.MobolizeVPN: {
      if (fieldName === 'dsid_supported') {
        updateAfProtocolVersionOptionsFor5GPOIs(template, data, originalTemplate, (fieldValue as unknown) as boolean);
      }
      if (fieldName === 'nodeNameType') {
        updateTemplateAndDataBasedOnNodeNameType(template, fieldValue, data, xcipioNodes);
      }
      break;
    }
    default: {
      break;
    }
  }
}

function copyUriOfSelectedNodeName(
  fieldName: any,
  fieldValue: unknown,
  data: Partial<AccessFunctionFormData>,
  networks?: Network[],
  accessFunctions?: AccessFunction[],
) {
  if (_.isArray(data.provisioningInterfaces) && data.provisioningInterfaces[0]?.role === 'SERVER') {
      return;
  }
  const serverAfs = accessFunctions?.filter((af) => SERVER_AF_TYPES.includes(af.type)) ?? [];
  const filteredNetworkIds = (networks ?? []).filter((n) => n.nodeName === fieldValue).map((n) => n.networkId);
  const serverAf = serverAfs.find((af) => {
    if (_.isArray(af.provisioningInterfaces)) {
      const ownnetid = af.provisioningInterfaces[0].options.find((o) => o.key === 'ownnetid');
      const decoded = Buffer.from(ownnetid?.value as string, 'base64').toString('binary') as string;
      return filteredNetworkIds.includes(decoded as string);
    }
    return false;
  });
  let serverDestUri = '';
  if (serverAf && _.isArray(serverAf.provisioningInterfaces)) {
    const uri = serverAf.provisioningInterfaces[0].options.find((o) => o.key === 'uri');
    serverDestUri = (uri?.value ?? '') as string;
  }
  if (_.isArray(data.provisioningInterfaces)) {
    data.provisioningInterfaces[0].uri = serverDestUri;
  }
}

function filterNetworksBasedOnNodeNameSelection(selectedNodeName = '', data: Partial<AccessFunctionFormData>, networks: Network[] = [], formTemplate: FormTemplateSection) {
  const filteredNetworks = (networks ?? []).filter((n: Network) => n.nodeName === selectedNodeName);
  formTemplate.fields.ownnetid.options = [];
  if (_.isArray(data.provisioningInterfaces) && data.provisioningInterfaces.length > 0) {
    if (filteredNetworks.length) {
      data.provisioningInterfaces[0].ownnetid = filteredNetworks[0].id;
    } else {
      data.provisioningInterfaces[0].ownnetid = '';
    }
  }
  setOptionForTemplateSection(formTemplate, getNetworkOptionsMap(filteredNetworks), ['ownnetid']);
}

function updateNodeNameFieldBasedOnRoleSelection(
  template: FormTemplate,
  role: unknown,
  data: Partial<AccessFunctionFormData>,
  nodeFormTemplateField: FormTemplateField,
  xcipioNodes: NodeData[] = [],
  networks: Network[] = [],
  accessFunctions: AccessFunction[] = [],
  formSection: FormTemplateSection
  ) {
  if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
    adaptLiXiNodeOptions(template, role, data, nodeFormTemplateField, xcipioNodes, networks, accessFunctions);
    if (template.key !== AFTypes.SS8MDF2
      && template.key !== AFTypes.SS8MDF3
      && template.key !== AFTypes.SS8CCPAG
      && template.key !== AFTypes.SS8XCF3) {
      if (role === 'SERVER') {
        formSection.fields.ownnetid.hide = 'false';
        if (formSection.metadata.layout && _.isArray(formSection.metadata.layout.rows)) {
          formSection.metadata.layout.rows[1] = formSection.metadata.layout.rows[1].slice(0, 4);
        }
      } else {
        formSection.fields.ownnetid.hide = 'true';
        if (formSection.metadata.layout && _.isArray(formSection.metadata.layout.rows)) {
          if (formSection.metadata.layout.rows[1].length < 5) {
            formSection.metadata.layout.rows[1] = [...formSection.metadata.layout.rows[1], '$empty'];
          }
        }
      }
    }
  }
}

function updateTemplateBasedOnDsidValue(
  template: FormTemplate,
  fieldValue: unknown,
  data: Partial<AccessFunctionFormData>,
  xcipioNodes?: NodeData[]) {
    if (template.key === AFTypes.MavenirLimaasMgr
      || template.key === AFTypes.MavenirPACOCMS
      || template.key === AFTypes.MavenirIMSCMS
      || template.key === AFTypes.NokiaSMF
      || template.key === AFTypes.NgVoiceLICF
      || template.key === AFTypes.MobolizeVPN) {
      return;
    }

    const checked = fieldValue as boolean;
    const formSection = (template.provisioningInterfaces as FormTemplateSection);
    if (checked) {
      toggleFieldVisibility(formSection, 'mdf2NodeName', 'mdf2NodeNameMultiSelect', false);
      const hasNodeNameTypeField = !!formSection.fields.nodeNameType;
      if (!hasNodeNameTypeField) {
        toggleFieldVisibility(formSection, 'mdf3NodeName', 'mdf3NodeNameMultiSelect', false);
      } else {
        const nodeNameType = getNodeNameType(data, xcipioNodes);
        if (nodeNameType === 'mdf3NodeName') {
          toggleFieldVisibility(formSection, 'mdf3NodeName', 'mdf3NodeNameMultiSelect', false);
        }
      }
    } else {
      toggleFieldVisibility(formSection, 'mdf2NodeName', 'mdf2NodeNameMultiSelect', true);
      const hasNodeNameTypeField = !!formSection.fields.nodeNameType;
      if (!hasNodeNameTypeField) {
        toggleFieldVisibility(formSection, 'mdf3NodeName', 'mdf3NodeNameMultiSelect', true);
      } else {
        const nodeNameType = getNodeNameType(data, xcipioNodes);
        if (nodeNameType === 'mdf3NodeName') {
          toggleFieldVisibility(formSection, 'mdf3NodeName', 'mdf3NodeNameMultiSelect', true);
        }
      }
    }
}

function updateTemplateAndDataBasedOnNodeNameType(
  template: FormTemplate,
  fieldValue: unknown,
  data: Partial<AccessFunctionFormData>,
  xcipioNodes?: NodeData[]
) {
  if (fieldValue === 'ccnodename') {
    data.mdf3NodeName = '';
    if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
      data.provisioningInterfaces[0].mdf3NodeNameMultiSelect = undefined;
    }
  }
  if (fieldValue === 'mdf3NodeName') {
    data.mdf3NodeName = '';
    if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
      data.provisioningInterfaces[0].mdf3NodeNameMultiSelect = undefined;
      updateTemplateBasedOnDsidValue(template, data.provisioningInterfaces[0].dsid_supported, data, xcipioNodes);
    }
  }
}

const clearSelections = (template: FormTemplate | undefined, data: Partial<AccessFunctionFormData>, mode = 'update') => {
  if (template == null) {
    return;
  }

  // clear already selected items
  if (data.provisioningInterfaces && data.provisioningInterfaces.length > 0) {
    (data.provisioningInterfaces[0] as AccessFunctionInterfaceFormData).mdf2NodeNameMultiSelect = [];
    (data.provisioningInterfaces[0] as AccessFunctionInterfaceFormData).mdf3NodeNameMultiSelect = [];
    if (mode === 'update') {
      (data.provisioningInterfaces[0] as AccessFunctionInterfaceFormData).mdf2NodeName = '';
      (data.provisioningInterfaces[0] as AccessFunctionInterfaceFormData).mdf3NodeName = '';
      data.mdf2NodeName = '';
      data.mdf3NodeName = '';
    }
  }
}

const toggleFieldVisibility = (formSection: FormTemplateSection, field1: string, field2: string, shouldShowField1: boolean) => {
  if (formSection == null) {
    return;
  }

  if (shouldShowField1) {
    setFieldVisibility(formSection, field1, true);
    setFieldVisibility(formSection, field2, false);
  } else {
    setFieldVisibility(formSection, field1, false);
    setFieldVisibility(formSection, field2, true);
  }
}

const setFieldVisibility = (formSection: FormTemplateSection, field: string, isVisible: boolean) => {
  if (formSection == null) {
    return;
  }
  if (isVisible) {
    if (formSection?.fields[field]) {
      formSection.fields[field].hide = 'false';
    }
    return;
  }
  if (formSection?.fields[field]) {
    formSection.fields[field].hide = 'true';
  }
}

const getNodeNameType = (data: Partial<AccessFunctionFormData>, xcipioNodes?: NodeData[]) => {
    const nodeNames = data.mdf3NodeName;
    let firstNodeName: string | undefined = '';

    firstNodeName = nodeNames?.substring(0, nodeNames.indexOf(' '));
    if (firstNodeName === '') {
      firstNodeName = nodeNames;
    }
    const node = xcipioNodes?.find((node) => node.nodeName === firstNodeName);
    let nodeNameType = node?.nodeType === 'MDF3' ? 'mdf3NodeName' : 'ccnodename';

    if (data.provisioningInterfaces && data.provisioningInterfaces[0]?.nodeNameType) {
      nodeNameType = data.provisioningInterfaces[0]?.nodeNameType;
    }
    return nodeNameType;
}

const updateAfProtocolVersionOptionsFor5GPOIs = (template: FormTemplate | undefined, data: Partial<AccessFunctionFormData>, originalTemplate?: FormTemplate | undefined, fieldValue?: boolean): void => {
  if (template == null || !originalTemplate || fieldValue === undefined) {
    return;
  }

  const field = (template.provisioningInterfaces as FormTemplateSection).fields.version;
  const options = (field.options ?? []);
  if (field.type === 'select' && options.length > 0) {
    let filteredVersions: FormTemplateOption[] | undefined = [];

    if (fieldValue) {
      filteredVersions = options.filter((v) => semver.gte(v.value, '1.14.0')).sort((a, b) => semver.rcompare(a.value, b.value));
    } else {
      filteredVersions = (originalTemplate.provisioningInterfaces as FormTemplateSection).fields.version.options;
    }

    field.options = filteredVersions;
    if (field.options?.length) {
      const initialValue = field.options[0]?.value ?? '';

      if (data?.provisioningInterfaces?.length) {
        const { version } = data.provisioningInterfaces[0];
        const isVersionValid = filteredVersions?.find((v) => v.value === version);

        field.initial = isVersionValid ? version : initialValue;
      } else {
        field.initial = initialValue;
      }

      if (data.provisioningInterfaces && data.provisioningInterfaces[0]?.version) {
        data.provisioningInterfaces[0].version = field.initial;
      }
    }
  }
}

export function adaptTemplate5GForGRExisting(
  template: FormTemplate | undefined,
  formData: AccessFunctionFormData,
  xcipioNodes: NodeData[] = [],
  networks: Network[] = [],
  accessFunctions: AccessFunction[] = [],
  originalTemplates?: { [key: string]: FormTemplate; } | undefined
): FormTemplate | undefined {
  if (_.isNil(template)) {
    return;
  }

  const clonedTemplate = cloneDeep(template);
  clonedTemplate.formId = Date.now().toString();

  if (formData.provisioningInterfaces && formData.provisioningInterfaces.length > 0) {
    if (formData.provisioningInterfaces[0].dsid_supported
      || formData.tag === AFTypes.MavenirLimaasMgr
      || formData.tag === AFTypes.MavenirIMSCMS
      || formData.tag === AFTypes.MavenirPACOCMS
      || formData.tag === AFTypes.NokiaSMF
      || formData.tag === AFTypes.NgVoiceLICF
      || formData.tag === AFTypes.MobolizeVPN) {
      const checked = !!formData.provisioningInterfaces[0].dsid_supported as boolean;

      if (originalTemplates) {
        updateAfProtocolVersionOptionsFor5GPOIs(clonedTemplate, formData, originalTemplates[template.key] ?? undefined, (!!checked as unknown) as boolean);
      }
      clearSelections(clonedTemplate, formData, 'create');
      updateTemplateBasedOnDsidValue(clonedTemplate, !!checked, formData, xcipioNodes);

      const selectedMdf2s = formData.provisioningInterfaces[0].mdf2NodeName?.split(' ') || [];
      const selectedMdf3s = formData.provisioningInterfaces[0].mdf3NodeName?.split(' ') || [];
      if (selectedMdf2s.length > 0) {
        formData.provisioningInterfaces[0].mdf2NodeNameMultiSelect = selectedMdf2s.map((o: string) => { return { label: o, value: o }; });
      }
      if (selectedMdf3s.length > 0) {
        formData.provisioningInterfaces[0].mdf3NodeNameMultiSelect = selectedMdf3s.map((o: string) => { return { label: o, value: o }; });
      }
    }
    const formSection = (clonedTemplate.provisioningInterfaces as FormTemplateSection);
    switch (template.key) {
      case AFTypes.SS8MDF2: {
        if (_.isArray(formData.provisioningInterfaces) && formData.provisioningInterfaces[0].role === 'SERVER') {
          const netId = formData.provisioningInterfaces[0].ownnetid;
          const network = networks.find((n) => n.id === netId) as Network;
          formData.provisioningInterfaces[0].li_x1_mdf2nodename = (network.nodeName) ?? '';
          filterNetworksBasedOnNodeNameSelection(formData.provisioningInterfaces[0].li_x1_mdf2nodename as string, formData, networks, formSection);
        }
        updateNodeNameFieldBasedOnRoleSelection(clonedTemplate, formData.provisioningInterfaces[0].role, formData, formSection.fields.li_x1_mdf2nodename, xcipioNodes, networks, accessFunctions, formSection);
        break;
      }
      case AFTypes.SS8MDF3: {
        if (_.isArray(formData.provisioningInterfaces) && formData.provisioningInterfaces[0].role === 'SERVER') {
          const netId = formData.provisioningInterfaces[0].ownnetid;
          const network = networks.find((n) => n.id === netId) as Network;
          formData.provisioningInterfaces[0].li_x1_mdf3nodename = (network.nodeName) ?? '';
          filterNetworksBasedOnNodeNameSelection(formData.provisioningInterfaces[0].li_x1_mdf3nodename as string, formData, networks, formSection);
        }
        updateNodeNameFieldBasedOnRoleSelection(clonedTemplate, formData.provisioningInterfaces[0].role, formData, formSection.fields.li_x1_mdf3nodename, xcipioNodes, networks, accessFunctions, formSection);
        break;
      }
      case AFTypes.SS8CCPAG: {
        if (_.isArray(formData.provisioningInterfaces) && formData.provisioningInterfaces[0].role === 'SERVER') {
          const netId = formData.provisioningInterfaces[0].ownnetid;
          const network = networks.find((n) => n.id === netId) as Network;
          formData.provisioningInterfaces[0].li_x1_ccpagnodename = (network.nodeName) ?? '';
          filterNetworksBasedOnNodeNameSelection(formData.provisioningInterfaces[0].li_x1_ccpagnodename as string, formData, networks, formSection);
        }
        updateNodeNameFieldBasedOnRoleSelection(clonedTemplate, formData.provisioningInterfaces[0].role, formData, formSection.fields.li_x1_ccpagnodename, xcipioNodes, networks, accessFunctions, formSection);
        break;
      }
      case AFTypes.SS8XCF3: {
        if (_.isArray(formData.provisioningInterfaces) && formData.provisioningInterfaces[0].role === 'SERVER') {
          const netId = formData.provisioningInterfaces[0].ownnetid;
          const network = networks.find((n) => n.id === netId) as Network;
          formData.provisioningInterfaces[0].li_x1_xcf3nodename = (network.nodeName) ?? '';
          filterNetworksBasedOnNodeNameSelection(formData.provisioningInterfaces[0].li_x1_xcf3nodename as string, formData, networks, formSection);
        }
        updateNodeNameFieldBasedOnRoleSelection(clonedTemplate, formData.provisioningInterfaces[0].role, formData, formSection.fields.li_x1_xcf3nodename, xcipioNodes, networks, accessFunctions, formSection);
        break;
      }
      default: {
        break;
      }
    }
  }
  return clonedTemplate;

}

function adaptLiXiNodeOptions(
  template: FormTemplate,
  role: unknown,
  data: Partial<AccessFunctionFormData>,
  nodeFormTemplateField: FormTemplateField,
  xcipioNodes: NodeData[] = [],
  networks: Network[] = [],
  accessFunctions: AccessFunction[] = []) {
  const ccpagOptions = getXcipioNodesByType(xcipioNodes, ['CCPAG', 'XCP']);
  const xcf3Options = getXcipioNodesByType(xcipioNodes, ['XCF3', 'XCP']);
  const mdf2Options = getXcipioNodesByType(xcipioNodes, ['MDF2', 'XCP']);
  const mdf3Options = getXcipioNodesByType(xcipioNodes, ['MDF3', 'XCP']);
  if (role === 'CLIENT') {
    setOptionsForLiX1Fields(template, accessFunctions, networks, ccpagOptions, xcf3Options, mdf2Options, mdf3Options);
  } else if (role === 'SERVER') {
    if (template.key === AFTypes.SS8MDF2) {
      setOptionsForFields(template, mdf2Options, ['li_x1_mdf2nodename']);
    } else if (template.key === AFTypes.SS8MDF3) {
      setOptionsForFields(template, mdf3Options, ['li_x1_mdf3nodename']);
    } else if (template.key === AFTypes.SS8CCPAG) {
      setOptionsForFields(template, ccpagOptions, ['li_x1_ccpagnodename']);
    } else if (template.key === AFTypes.SS8XCF3) {
      setOptionsForFields(template, xcf3Options, ['li_x1_xcf3nodename']);
    }
  }
}

export function adapt5GAfsForGRToSubmit(accessFunctionForm: AccessFunctionFormData): void {
  switch (accessFunctionForm.tag) {
    case AFTypes.CasaSMF: {
      break;
    }
    case AFTypes.EricssonAMF: {
      break;
    }
    case AFTypes.Ericsson5GNEF: {
      break;
    }
    case AFTypes.Ericsson5GSMF: {
      break;
    }
    case AFTypes.Mavenir5GMMSC: {
      break;
    }
    case AFTypes.MavenirLimaasMgr: {
      break;
    }
    case AFTypes.MavenirIMSCMS: {
      break;
    }
    case AFTypes.MavenirPACOCMS: {
      break;
    }
    case AFTypes.MavenirSMSF: {
      break;
    }
    case AFTypes.Nokia5GMMSC: {
      break;
    }
    case AFTypes.NokiaSMF: {
      break;
    }
    case AFTypes.NokiaUDM: {
      break;
    }
    case AFTypes.NokiaTAS: {
      break;
    }
    case AFTypes.SS8CCPAG: {
      break;
    }
    case AFTypes.SS8XCF3: {
      break;
    }
    case AFTypes.SS8MDF2: {
      break;
    }
    case AFTypes.SS8MDF3: {
      break;
    }
    case AFTypes.NgVoiceLICF: {
      break;
    }
    case AFTypes.MobolizeVPN: {
      break;
    }
    default: {
      break;
    }
  }
  adaptMultiSelectMdfForSubmit(accessFunctionForm);
  if (accessFunctionForm.provisioningInterfaces && accessFunctionForm.provisioningInterfaces.length > 0) {
    const firstItem = accessFunctionForm.provisioningInterfaces[0];
    if (firstItem?.role === 'SERVER' && !accessFunctionForm.type.includes('_SVR')) {
      accessFunctionForm.type += '_SVR';
    }
  }
}

function adaptMultiSelectMdfForSubmit(accessFunctionForm: AccessFunctionFormData): void {
  if (accessFunctionForm.provisioningInterfaces && accessFunctionForm.provisioningInterfaces.length > 0) {
    if (accessFunctionForm.provisioningInterfaces[0]?.dsid_supported
      || accessFunctionForm.tag === AFTypes.MavenirLimaasMgr
      || accessFunctionForm.tag === AFTypes.MavenirIMSCMS
      || accessFunctionForm.tag === AFTypes.MavenirPACOCMS
      || accessFunctionForm.tag === AFTypes.NokiaSMF
      || accessFunctionForm.tag === AFTypes.NgVoiceLICF
      || accessFunctionForm.tag === AFTypes.MobolizeVPN) {
      const mdf2Values = (accessFunctionForm.provisioningInterfaces[0]?.mdf2NodeNameMultiSelect || [])
        .map((x: FormTemplateOption) => x.value)
        .reduce((acc, curr) => {
          acc += ` ${curr}`;
          return acc;
        }, '');

      const mdf3Values = (accessFunctionForm.provisioningInterfaces[0]?.mdf3NodeNameMultiSelect || [])
        .map((x: FormTemplateOption) => x.value)
        .reduce((acc, curr) => {
          acc += ` ${curr}`;
          return acc;
        }, '');

      if (mdf2Values.length > 0) {
        accessFunctionForm.provisioningInterfaces[0].mdf2NodeName = (mdf2Values as string).substring(1);
        accessFunctionForm.mdf2NodeName = accessFunctionForm.provisioningInterfaces[0].mdf2NodeName;
      }

      if (mdf3Values.length > 0) {
        let nodeNameType = 'mdf3NodeName';
        if (accessFunctionForm.provisioningInterfaces && accessFunctionForm.provisioningInterfaces[0]?.nodeNameType) {
          nodeNameType = accessFunctionForm.provisioningInterfaces[0]?.nodeNameType;
        }
        if (nodeNameType === 'mdf3NodeName') {
          accessFunctionForm.provisioningInterfaces[0].mdf3NodeName = (mdf3Values as string).substring(1);
          accessFunctionForm.mdf3NodeName = accessFunctionForm.provisioningInterfaces[0].mdf3NodeName;
        }
      }
    }

    accessFunctionForm.provisioningInterfaces[0].mdf2NodeNameMultiSelect = undefined;
    accessFunctionForm.provisioningInterfaces[0].mdf3NodeNameMultiSelect = undefined;
  }
}
